import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  ExternalLink,
  CheckCircle2,
  ArrowRight,
  Layers,
  ShoppingBag,
  Building2,
  Zap,
  Sparkles,
  Clock,
  Truck
} from 'lucide-react';

const Clients = () => {
  const { t, language } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');

  const showcases = [
    {
      id: 'catalogohub',
      name: 'Catálogo Hub',
      category: ['saas', 'ecommerce'],
      isFeatured: true,
      badge: language === 'es' ? '🚀 Flagship SaaS Vegsoft' : '🚀 Flagship Vegsoft SaaS',
      status: language === 'es' ? 'Lanzado' : 'Live',
      statusClass: 'badge-status-green',
      logo: '/assets/catalogo-hub-logo.webp',
      logoFallback: '/assets/catalogo-hub-logo.jpg',
      url: 'https://catalog.vegsoftsolutions.com/',
      description: language === 'es'
        ? 'Plataforma SaaS para creación de catálogos digitales interactivos en tiempo real con pedidos directos a WhatsApp, cotizador instantáneo y control de inventario.'
        : 'SaaS platform for publishing real-time interactive digital catalogs with automated WhatsApp orders, instant quotation, and inventory control.',
      features: language === 'es'
        ? ['Catálogo 3D/Web', 'Pedidos WhatsApp', 'Control de Stock', 'Cotizador en Vivo']
        : ['3D/Web Catalog', 'WhatsApp Orders', 'Live Inventory', 'Instant Quotes'],
      ctaText: language === 'es' ? 'Explorar Catálogo Hub' : 'Explore Catálogo Hub',
      tag: language === 'es' ? 'Multi-tienda • Retail' : 'Multi-store • Retail'
    },
    {
      id: 'barbershop',
      name: 'Vegsoft Barber',
      category: ['saas'],
      isFeatured: false,
      badge: language === 'es' ? 'SaaS Reservas' : 'Booking SaaS',
      status: language === 'es' ? 'Beta' : 'Beta',
      statusClass: 'badge-status-purple',
      logo: '/assets/barber-logo.jpg',
      logoFallback: '/assets/barber-logo.jpg',
      url: 'https://barbershop.vegsoftsolutions.com/',
      description: language === 'es'
        ? 'Software SaaS de gestión operativa, reservas 24/7 y recordatorios inteligentes para barberías, spas y centros de estética.'
        : 'SaaS management software with 24/7 automated reservations, multi-branch operations, and smart reminders for salons.',
      features: language === 'es'
        ? ['Agenda Automática', 'Cobros Online', 'Multi-sucursal', 'Recordatorios']
        : ['Auto Booking', 'Online Payments', 'Multi-location', 'Smart Reminders'],
      ctaText: language === 'es' ? 'Ver Plataforma' : 'View Platform',
      tag: language === 'es' ? 'Agenda & Pagos Online' : 'Scheduling & Payments'
    },
    {
      id: 'voycargo',
      name: 'VoyCargo',
      category: ['ecommerce', 'enterprise'],
      isFeatured: false,
      badge: language === 'es' ? 'Logística & Transporte' : 'Logistics & Freight',
      status: language === 'es' ? 'En Desarrollo' : 'In Development',
      statusClass: 'badge-status-amber',
      logo: '/assets/voycargo-logo.webp',
      logoFallback: '/assets/voycargo-logo.png',
      url: 'https://voycargo.cl/',
      description: language === 'es'
        ? 'Plataforma de logística y transporte de carga con asignación inteligente de rutas, app móvil nativa para choferes y tracking satelital GPS en tiempo real.'
        : 'Freight logistics platform featuring intelligent route assignment, native mobile driver app, and real-time satellite GPS tracking.',
      features: language === 'es'
        ? ['App iOS & Android', 'Tracking Satelital', 'Despacho Inteligente', 'Panel Web']
        : ['iOS & Android App', 'Live GPS Tracking', 'Smart Dispatch', 'Web Portal'],
      ctaText: language === 'es' ? 'Ver Plataforma' : 'View Platform',
      tag: language === 'es' ? 'App & Tracking GPS' : 'App & GPS Tracking'
    },
    {
      id: 'mueblesnew',
      name: 'Muebles New',
      category: ['ecommerce'],
      isFeatured: false,
      badge: language === 'es' ? 'E-commerce & Retail' : 'E-commerce & Retail',
      status: language === 'es' ? 'En Producción' : 'Live Store',
      statusClass: 'badge-status-green',
      logo: '/assets/mueblesnew-logo.png',
      logoFallback: '/assets/mueblesnew-logo.png',
      url: 'https://www.mueblesnew.cl/',
      description: language === 'es'
        ? 'Tienda e-commerce de alta conversión con catálogo interactivo, configurador dinámico de productos, carrito veloz y pasarelas de pago online.'
        : 'High-conversion e-commerce platform with dynamic product catalog, fast shopping cart, and secure online payment gateways.',
      features: language === 'es'
        ? ['Catálogo 3D/Web', 'Pago en Línea', 'Carrito Rápido', 'Mobile-First']
        : ['3D/Web Catalog', 'Online Payments', 'Fast Checkout', 'Mobile-First'],
      ctaText: language === 'es' ? 'Ver Tienda' : 'View Store',
      tag: language === 'es' ? 'E-commerce Transaccional' : 'Transactional Store'
    },
    {
      id: 'sfingenieria',
      name: 'SF Ingeniería',
      category: ['enterprise'],
      isFeatured: false,
      badge: language === 'es' ? 'Portal Corporativo B2B' : 'Corporate Portal B2B',
      status: language === 'es' ? 'En Producción' : 'Live Portal',
      statusClass: 'badge-status-green',
      logo: '/assets/sfingenieria-logo.webp',
      logoFallback: '/assets/sfingenieria-logo.png',
      url: 'https://sfingenieria.cl/',
      description: language === 'es'
        ? 'Portal corporativo y gestión técnica para ingeniería de gran escala, minería, montajes industriales y supervisión de proyectos de alta envergadura.'
        : 'Corporate portal and operational management system for large-scale engineering, industrial assemblies, and infrastructure projects.',
      features: language === 'es'
        ? ['Portal Corporativo', 'Gestión Técnica', 'Alta Disponibilidad', 'Cloud 99.9%']
        : ['Corporate Web', 'Technical Docs', 'High Availability', '99.9% Uptime'],
      ctaText: language === 'es' ? 'Ver Portal' : 'View Portal',
      tag: language === 'es' ? 'Ingeniería & Minería' : 'Engineering & Mining'
    }
  ];

  const filterTabs = [
    { id: 'all', label: language === 'es' ? 'Todos los Productos' : 'All Products', count: 5, icon: Layers },
    { id: 'saas', label: language === 'es' ? 'SaaS Vegsoft' : 'Vegsoft SaaS', count: 2, icon: Zap },
    { id: 'ecommerce', label: language === 'es' ? 'E-commerce & Logística' : 'E-commerce & Logistics', count: 3, icon: ShoppingBag },
    { id: 'enterprise', label: language === 'es' ? 'Empresas & B2B' : 'Enterprise & B2B', count: 2, icon: Building2 }
  ];

  const filteredShowcases = activeFilter === 'all'
    ? showcases
    : showcases.filter(item => item.category.includes(activeFilter));

  const featuredProduct = showcases.find(s => s.id === 'catalogohub');

  return (
    <section
      id="products"
      data-testid="clients-section"
      className="relative py-20 lg:py-28 overflow-hidden bg-white border-t border-slate-100"
    >
      {/* Anchor compatibility */}
      <div id="clients" className="absolute -top-24 left-0 w-0 h-0" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="stripe-badge mb-4 inline-flex">
            <span>✨</span>
            <span>{language === 'es' ? 'Ecosistema de Software Propio & Casos' : 'Proprietary Ecosystem & Case Studies'}</span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mb-4 tracking-tight text-[#0F172A]">
            {language === 'es' ? (
              <>
                Nuestras Soluciones SaaS y{' '}
                <span className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">
                  Plataformas Activas
                </span>
              </>
            ) : (
              <>
                Proprietary SaaS Platforms &{' '}
                <span className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">
                  Active Deployments
                </span>
              </>
            )}
          </h2>
          <p className="text-base lg:text-lg text-slate-500 leading-relaxed font-normal">
            {language === 'es'
              ? 'Concebimos, programamos y operamos productos SaaS propios como Catálogo Hub y Vegsoft Barber, además de crear plataformas a medida para empresas en expansión.'
              : 'We architect and operate proprietary SaaS platforms like Catálogo Hub and Vegsoft Barber, alongside bespoke platforms for growing enterprises.'}
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {filterTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? 'bg-[#0F172A] text-white shadow-sm scale-[1.02]'
                    : 'bg-slate-100 text-slate-600 hover:text-[#0F172A] hover:bg-slate-200'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                <span>{tab.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-white text-slate-600 shadow-sm'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Featured Catálogo Hub Card — shown in "all" view */}
        {activeFilter === 'all' && featuredProduct && (
          <div className="mb-10">
            <div className="stripe-card-featured p-6 lg:p-8">
              <div className="grid lg:grid-cols-12 gap-8 items-center">
                
                {/* Visual Preview */}
                <div className="lg:col-span-4 bg-slate-50 rounded-2xl p-6 flex flex-col items-center justify-center border border-slate-100">
                  <div className="w-full max-w-[200px] aspect-square rounded-2xl bg-white p-4 flex flex-col items-center justify-center shadow-sm border border-slate-100">
                    <picture>
                      <source srcSet="/assets/catalogo-hub-logo.webp" type="image/webp" />
                      <img
                        src="/assets/catalogo-hub-logo.jpg"
                        alt="Catálogo Hub"
                        width="150"
                        height="150"
                        fetchPriority="high"
                        loading="eager"
                        decoding="async"
                        className="w-28 h-28 object-contain rounded-xl"
                      />
                    </picture>
                    <div className="mt-3">
                      <span className="badge-status-green">
                        ● {language === 'es' ? 'Lanzado • SaaS Activo' : 'Live SaaS'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="lg:col-span-8 flex flex-col justify-between space-y-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="px-3 py-1 rounded-full bg-indigo-50 text-[#4F46E5] text-xs font-bold uppercase tracking-wider">
                        ⭐ {language === 'es' ? 'Flagship SaaS' : 'Flagship SaaS'}
                      </span>
                      <span className="text-xs font-semibold text-slate-400">
                        catalog.vegsoftsolutions.com
                      </span>
                    </div>

                    <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#0F172A] tracking-tight">
                      Catálogo Hub
                    </h3>

                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed mt-2">
                      {language === 'es'
                        ? 'La plataforma creada por Vegsoft para crear catálogos digitales interactivos en minutos. Conecta tus productos con clientes, genera cotizaciones automáticas en vivo y recibe pedidos limpios y estructurados directamente en WhatsApp.'
                        : 'The definitive platform engineered by Vegsoft to deploy interactive catalogs in minutes. Connect products with buyers, generate instant quotes, and route clean orders straight to WhatsApp.'}
                    </p>

                    {/* Features list */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-4">
                      {[
                        { title: language === 'es' ? 'Catálogo 3D/Web' : '3D/Web Catalog', desc: language === 'es' ? 'Ultrarrápido y dinámico' : 'Fast and interactive' },
                        { title: language === 'es' ? 'Pedidos a WhatsApp' : 'WhatsApp Orders', desc: language === 'es' ? '1 click directo al chat' : '1-click to chat' },
                        { title: language === 'es' ? 'Control de Stock' : 'Stock Control', desc: language === 'es' ? 'Sincronizado en tiempo real' : 'Real-time sync' },
                        { title: language === 'es' ? 'Cotizador en Vivo' : 'Live Quoter', desc: language === 'es' ? 'Cálculo dinámico de precios' : 'Instant pricing' },
                      ].map((feat, idx) => (
                        <div key={idx} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-[#0F172A] mb-1">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] shrink-0" />
                            <span>{feat.title}</span>
                          </div>
                          <div className="text-[11px] text-slate-500">{feat.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <a
                      href="https://catalog.vegsoftsolutions.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-stripe-dark text-sm"
                    >
                      <span>{language === 'es' ? 'Visitar Catálogo Hub' : 'Visit Catálogo Hub'}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <a
                      href="#contact"
                      className="btn-stripe-white text-sm"
                    >
                      <span>{language === 'es' ? 'Solicitar Demo' : 'Request Demo'}</span>
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {filteredShowcases
            .filter(item => activeFilter !== 'all' || !item.isFeatured)
            .map((item, index) => (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`client-showcase-${index}`}
                className="stripe-card p-6 flex flex-col justify-between group overflow-hidden"
              >
                {/* Status & External link */}
                <div className="flex items-center justify-between mb-4">
                  <span className={item.statusClass}>
                    ● {item.status}
                  </span>
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-[#4F46E5] group-hover:text-white transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </div>
                </div>

                {/* Logo Box */}
                <div className="mb-4 p-4 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center h-24 group-hover:border-slate-200 transition-colors">
                  <picture>
                    <source srcSet={item.logo} type="image/webp" />
                    <img
                      src={item.logoFallback || item.logo}
                      alt={item.name}
                      width="160"
                      height="64"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      className="max-h-14 w-auto max-w-[150px] object-contain group-hover:scale-105 transition-transform duration-200 rounded-lg"
                    />
                  </picture>
                </div>

                {/* Content */}
                <div className="flex-grow flex flex-col justify-between">
                  <div>
                    <div className="text-[11px] font-bold mb-1 uppercase tracking-wider text-[#4F46E5]">
                      {item.badge}
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-1 text-[#0F172A] group-hover:text-[#4F46E5] transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-xs text-slate-500 leading-relaxed mb-3">
                      {item.description}
                    </p>
                  </div>

                  {/* Feature tags */}
                  <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-1.5">
                    {item.features.map((feat, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-50 text-[10px] font-semibold text-slate-600 border border-slate-100"
                      >
                        <CheckCircle2 className="w-2.5 h-2.5 text-[#10B981] shrink-0" />
                        <span>{feat}</span>
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 pt-3 flex items-center justify-between text-xs font-bold text-[#4F46E5] group-hover:text-[#3730A3] transition-colors">
                    <span>{item.ctaText}</span>
                    <ArrowRight className="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </a>
            ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="text-center mt-14 p-8 lg:p-10 rounded-3xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50 max-w-4xl mx-auto shadow-sm">
          <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#0F172A] mb-2">
            {language === 'es'
              ? '¿Listo para lanzar tu propia plataforma digital o SaaS?'
              : 'Ready to build your digital product or SaaS platform?'}
          </h3>
          <p className="text-slate-600 text-sm sm:text-base max-w-xl mx-auto mb-6">
            {language === 'es'
              ? 'Validamos tu modelo, diseñamos la arquitectura y construimos un prototipo funcional con rapidez y precisión.'
              : 'We validate your model, design the architecture, and deliver a functional prototype with speed and precision.'}
          </p>
          <a
            href="#contact"
            className="btn-stripe-dark text-sm"
          >
            <span>{language === 'es' ? 'Conversar sobre tu Proyecto' : 'Discuss Your Project'}</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Clients;
